import type React from 'react'
import {
  SELECTION_BADGE_TILE_FRAME_PATH,
  SELECTION_BADGE_TILE_INNER_PATH,
} from '@/components/ui/custom-icons'
import { PORT_HIT_SIZE } from '@/lib/text-linking/text-linking-geometry'

// In/out port body colour. The tile GEOMETRY is no longer expressed as a constant —
// `PortBadgeContainer` draws the shared `SELECTION_BADGE_TILE_*` paths instead, so a port and an
// overflow badge at the same coordinate are identical rather than half a pixel apart (WMPCM-9199).
// The old local rects were an 18x18 r3.5 body on a 20x20 r4.5 halo; the shared paths are r4 on r5,
// which is what Figma specifies.
//
// The port spec node this used to cite (17764:243563) no longer exists in the file. The nearest
// live reference is the "Add Text Link" icon container, node 15269:254143, which carries no
// stroke on the body — `PortBadgeContainer` matches that exactly now (WMPCM-9199); it used to keep
// a dashed/solid stroke as an open/linked cue, resolved by decision to drop it as redundant with
// the glyph/numeral already painted on top.
const PORT_FILL_COLOR = '#F5F5F5'

/**
 * The halo + body a port badge is drawn on: a translucent white ring around a rounded square.
 * Shared by `PortSquare`, `LinkedOutPortSquare` and the unlinked out-port "+", so the three read as
 * one component family.
 *
 * Drawn from the SAME paths as the lock/freeze/overflow selection badges
 * (`SELECTION_BADGE_TILE_*`, `custom-icons.tsx`) rather than from local rects. That is not merely
 * de-duplication: the unlinked "+" is deliberately coincident with `TextOverflowBadge`'s tile, and
 * the local rects were r3.5/r4.5 against the badge's r4/r5, so the two were visibly half a pixel
 * apart at the same coordinate (WMPCM-9199).
 *
 * The paths are authored on a 24 viewBox with the tile inset 3, and 24 is exactly `PORT_HIT_SIZE`
 * — hence the single `translate` that re-centres them on the local origin, where every caller has
 * already translated and (where it applies) rotated its group. No counter-scale to account for:
 * this whole layer is screen space now. The paths carry no width/height, so this adds no second
 * `rect[width="24"]` for `text-linking-layer.test.tsx`'s hit-rect queries to trip over.
 *
 * Paint order matters and matches `SelectionBadgeTileChrome`: body first, ring over it.
 *
 * No stroke on the body (WMPCM-9199) — matches the Figma reference (node 15269:254143) exactly,
 * the same across every port state. This used to carry a dashed/solid stroke as an open-vs-linked
 * cue, plus a hover-to-red recolor on the linked out-port's unlink affordance; both were redundant
 * with cues already conveyed elsewhere (the "+"/numeral/"×" glyph painted on top, and — for the
 * unlink hover — the "×" itself already turns red independently of this container).
 */
export function PortBadgeContainer(): React.JSX.Element {
  return (
    <g
      transform={`translate(${-PORT_HIT_SIZE / 2}, ${-PORT_HIT_SIZE / 2})`}
      pointerEvents="none"
      aria-hidden="true"
    >
      <path d={SELECTION_BADGE_TILE_INNER_PATH} fill={PORT_FILL_COLOR} />
      <path d={SELECTION_BADGE_TILE_FRAME_PATH} fill="#FFFFFF" fillOpacity={0.8} />
    </g>
  )
}
